/**
 * In-memory job post preview from the hiring "create job" form (`/jobs/:jobId` with this slug + router state).
 * Must stay in sync with `mapJobPostRow` job shape from the hiring job-posts API.
 */
export const JOB_POST_PREVIEW_ROUTE_SLUG = "preview";

function buildDisplayLocationLine(cityRaw, countryRaw) {
  const city = String(cityRaw ?? "").trim();
  const country = String(countryRaw ?? "").trim().toUpperCase();
  if (city && country) return `${city}, ${country}`;
  if (city) return city;
  if (country) return country;
  return "";
}

function readOrganizationNameForPreview(user) {
  if (!user || typeof user !== "object") return "";
  const rp = user.recruitmentProfile;
  const fromRp = typeof rp?.organizationName === "string" ? rp.organizationName.trim() : "";
  if (fromRp) return fromRp;
  const top = typeof user.companyName === "string" ? user.companyName.trim() : "";
  return top || "";
}

function readCompanyWebsiteForPreview(user) {
  if (!user || typeof user !== "object") return "";
  const rp = user.recruitmentProfile;
  const fromTop = typeof user.companyWebsite === "string" ? user.companyWebsite.trim() : "";
  if (fromTop) return fromTop;
  const careers = typeof rp?.careersUrl === "string" ? rp.careersUrl.trim() : "";
  return careers || "";
}

function readCompanyLogoDataUrlForPreview(user) {
  if (!user || typeof user !== "object") return "";
  const rp = user.recruitmentProfile;
  const fromRp = typeof rp?.companyLogoDataUrl === "string" ? rp.companyLogoDataUrl.trim() : "";
  if (fromRp) return fromRp;
  const top = typeof user.companyLogoDataUrl === "string" ? user.companyLogoDataUrl.trim() : "";
  return top || "";
}

function normalizeSocialProfilesForPreview(raw) {
  if (!raw || typeof raw !== "object") {
    return { linkedin: "", instagram: "", youtube: "", customs: [] };
  }
  return {
    linkedin: typeof raw.linkedin === "string" ? raw.linkedin : "",
    instagram: typeof raw.instagram === "string" ? raw.instagram : "",
    youtube: typeof raw.youtube === "string" ? raw.youtube : "",
    customs: Array.isArray(raw.customs) ? raw.customs : [],
  };
}

export function createEmptyPreviewJobPost() {
  return {
    id: JOB_POST_PREVIEW_ROUTE_SLUG,
    status: "published",
    title: "",
    company: "",
    companyWebsite: "",
    socialProfiles: normalizeSocialProfilesForPreview(null),
    companyLogoUrl: "",
    functionalArea: "",
    country: "",
    city: "",
    location: "",
    seniorityLevel: "",
    workMode: "",
    employmentType: "",
    salaryCurrency: "",
    salaryMin: "",
    salaryMax: "",
    deadline: "",
    summary: "",
    responsibilities: "",
    requirements: "",
    pitchVideoUrl: "",
    publishedAt: null,
    defaultApplyAudience: "anyone",
    defaultApplyCountryCodes: [],
  };
}

/**
 * @param {object} formData — hiring job create form state (fields align with persisted job_posts columns).
 * @param {string} savedPitchUrl — uploaded URL or blob: for an in-browser pitch preview.
 */
export function buildJobPostPreviewSnapshot(formData, savedPitchUrl, currentUser) {
  const city = String(formData?.city ?? "").trim();
  const country = String(formData?.country ?? "").trim().toUpperCase();
  const aud = formData?.defaultApplyAudience === "countries" ? "countries" : "anyone";
  const rawCodes = formData?.defaultApplyCountryCodes;
  const codes = [];
  if (Array.isArray(rawCodes)) {
    const seen = new Set();
    for (const item of rawCodes) {
      const c = String(item ?? "").trim().toUpperCase();
      if (!/^[A-Z]{2}$/.test(c) || seen.has(c)) continue;
      seen.add(c);
      codes.push(c);
    }
  }

  return {
    id: JOB_POST_PREVIEW_ROUTE_SLUG,
    status: "published",
    title: String(formData?.title ?? "").trim(),
    company: readOrganizationNameForPreview(currentUser),
    companyWebsite: readCompanyWebsiteForPreview(currentUser),
    socialProfiles: normalizeSocialProfilesForPreview(currentUser?.socialProfiles),
    companyLogoUrl: readCompanyLogoDataUrlForPreview(currentUser),
    functionalArea: String(formData?.functionalArea ?? "").trim(),
    country,
    city,
    location: buildDisplayLocationLine(city, country),
    seniorityLevel: String(formData?.seniorityLevel ?? "").trim(),
    workMode: String(formData?.workMode ?? "").trim(),
    employmentType: String(formData?.employmentType ?? "").trim(),
    salaryCurrency: String(formData?.salaryCurrency ?? "").trim(),
    salaryMin: String(formData?.salaryMin ?? "").trim(),
    salaryMax: String(formData?.salaryMax ?? "").trim(),
    deadline: "",
    summary: String(formData?.summary ?? "").trim(),
    responsibilities: String(formData?.responsibilities ?? ""),
    requirements: String(formData?.requirements ?? ""),
    pitchVideoUrl: String(savedPitchUrl ?? "").trim(),
    publishedAt: new Date().toISOString(),
    defaultApplyAudience: aud,
    defaultApplyCountryCodes: codes,
  };
}
